import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications';
import { useNavigate } from 'react-router-dom';
import NotificationToast from './NotificationToast';
import NotificationPermissionModal from './NotificationPermissionModal';

/**
 * NotificationBell: shows the notification icon with unread badge. On click
 * navigates to /notifications page. Toast and permission modal stay here;
 * list and mark-as-read live on the notifications page.
 */
interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    toastNotification,
    setToastNotification,
    unreadCount = 0,
    showPermissionModal,
    setShowPermissionModal,
    requestPermissionFromUser,
  } = useRealtimeNotifications({
    showBrowserNotification: true,
    showToast: true,
  });

  return (
    <>
      <NotificationPermissionModal
        isOpen={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        onAllow={requestPermissionFromUser}
        dontShowAgain
      />
      {toastNotification && (
        <NotificationToast
          notification={toastNotification}
          onClose={() => setToastNotification(null)}
          onClick={() => {
            setToastNotification(null);
            if (toastNotification.type === 'follow' && toastNotification.actor) {
              navigate(`/${toastNotification.actor.username || toastNotification.actor.id}`);
            } else if (toastNotification.post?.hashCode) {
              navigate(`/p/${toastNotification.post.hashCode}`);
            } else if (toastNotification.post?.id != null) {
              navigate(`/post/${toastNotification.post.id}`);
            } else {
              navigate('/notifications');
            }
          }}
        />
      )}

      <button
        onClick={() => navigate('/notifications')}
        className={`relative p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${className}`}
        aria-label={t('header.notifications')}
      >
        <div className="relative">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label={t('header.notifications')}>
            <title>{t('header.notifications')}</title>
            <path d="M16.792 3.904A4.989 4.989 0 0 1 21.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 0 1 4.708-5.218 4.21 4.21 0 0 1 3.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 0 1 3.679-1.938m0-2a6.04 6.04 0 0 0-4.797 2.127 6.052 6.052 0 0 0-4.787-2.127A6.985 6.985 0 0 0 .5 9.122c0 3.61 2.55 5.827 5.015 7.97.283.246.569.494.853.747l1.027.918a44.998 44.998 0 0 0 3.518 3.018 2 2 0 0 0 2.174 0 45.263 45.263 0 0 0 3.626-3.115l.922-.824c.293-.26.59-.519.885-.774 2.334-2.025 4.98-4.32 4.98-7.94a6.985 6.985 0 0 0-6.708-7.218Z" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-white dark:border-gray-800">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
      </button>
    </>
  );
};

export default NotificationBell;
